import { useI18n } from '@/hooks/useI18n';
import { ArrowUpRight, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, CartesianGrid, XAxis } from 'recharts';
import { useMemo, useState } from 'react';
import { useSchedulerStatus, useSchedulerRuns } from '@/hooks/queries/useScheduler';
import { useOverviewSeries } from '@/hooks/queries/useOverview';
import { formatDuration, formatNumber, formatRelative } from '@/lib/format';
import {
  SCHEDULER_RUN_TYPE_LABELS,
  SCHEDULER_RUN_TYPE_LABELS_EN,
  SCHEDULER_RUN_STATUS_LABELS,
  SCHEDULER_RUN_STATUS_LABELS_EN,
  SCHEDULER_RUN_TYPES,
  type SchedulerRunType,
} from '@/types/enums';
import type { OverviewSeriesPoint, RunSummary } from '@/types/api';
import { cn } from '@/lib/cn';

const RAIL_TABS = ['Followers', 'Viraux', 'Runs'] as const;
type Tab = (typeof RAIL_TABS)[number];

const SERIES_PERIOD = 30;

const TAB_PALETTE: Record<Tab, { stroke: string; gradientId: string }> = {
  Followers: { stroke: 'hsl(340 82% 62%)', gradientId: 'railGradFollowers' },
  Viraux: { stroke: 'hsl(32 95% 58%)', gradientId: 'railGradViraux' },
  Runs: { stroke: 'hsl(258 90% 66%)', gradientId: 'railGradRuns' },
};

export function RightRail() {
  const { t } = useI18n();
  const tabCaption = t(`${SERIES_PERIOD} derniers jours`, `Last ${SERIES_PERIOD} days`);
  const [tab, setTab] = useState<Tab>('Runs');
  const status = useSchedulerStatus();
  const runs = useSchedulerRuns({ size: 40 });
  const overviewSeries = useOverviewSeries(SERIES_PERIOD);

  const series = useMemo(() => {
    if (tab === 'Runs') return buildRunsChart(runs.data?.content ?? []);
    if (tab === 'Followers') return buildDailyChart(overviewSeries.data?.followers);
    return buildDailyChart(overviewSeries.data?.viralReels);
  }, [tab, runs.data, overviewSeries.data]);

  const isLoading = tab === 'Runs' ? runs.isLoading : overviewSeries.isLoading;
  const hasData = series.length > 0;

  const palette = TAB_PALETTE[tab];

  const recent = useMemo<RailActivityItem[]>(() => {
    if (!status.data?.lastRuns) return [];
    const icons: Record<SchedulerRunType, string> = {
      MAIN_3H: '⚙️',
      NIGHTLY_PROFILE: '👤',
      HOURLY_ROLLUP: '📊',
      DAILY_ROLLUP: '📅',
    };
    return SCHEDULER_RUN_TYPES.flatMap((type) => {
      const run = status.data?.lastRuns[type];
      if (!run) return [];
      return [
        {
          id: `last-${type}`,
          icon: icons[type],
          title: t(SCHEDULER_RUN_TYPE_LABELS[type], SCHEDULER_RUN_TYPE_LABELS_EN[type]),
          subtitle: t(
            SCHEDULER_RUN_STATUS_LABELS[run.status],
            SCHEDULER_RUN_STATUS_LABELS_EN[run.status],
          ),
          time: run.startedAt,
          status: run.status,
        },
      ];
    });
  }, [status.data, t]);

  return (
    <aside className="shrink-0 border-l border-line-1 bg-bg-0 px-5 py-5 xl:w-[340px] 2xl:w-[360px]">
      <section className="rounded-[16px] border border-line-1 bg-bg-1 p-4 shadow-card">
        <header className="flex items-start justify-between">
          <div>
            <h3 className="text-[15px] font-semibold tracking-tight text-text-1">
              {t('Aperçu', 'Overview')}
            </h3>
            <p className="text-[12px] text-text-3">
              {t('Durée des runs récents', 'Recent run duration')}
            </p>
          </div>
          <button
            type="button"
            aria-label={t('Ouvrir', 'Open')}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-line-1 text-text-3 transition-colors hover:text-text-1"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="mt-4 flex items-center gap-1 rounded-[10px] bg-bg-2 p-1">
          {RAIL_TABS.map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setTab(tabKey)}
              className={cn(
                'flex-1 rounded-[7px] px-2 py-1.5 text-[12px] font-medium transition-colors',
                tabKey === tab
                  ? 'bg-bg-1 text-text-1 shadow-[0_1px_2px_hsl(240_10%_12%/0.06)]'
                  : 'text-text-4 hover:text-text-2',
              )}
            >
              {tabKey === 'Viraux' ? t('Viraux', 'Viral') : tabKey}
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <span className="flex h-7 items-center gap-1 rounded-[8px] border border-line-1 px-2 text-[11px] text-text-3">
            {tab === 'Runs' ? t('40 derniers runs', 'Last 40 runs') : tabCaption}
          </span>
          <div className="ml-auto flex items-center gap-0.5">
            <button
              type="button"
              disabled
              className="flex h-6 w-6 items-center justify-center rounded-[6px] text-text-4"
              aria-label={t('Précédent', 'Previous')}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled
              className="flex h-6 w-6 items-center justify-center rounded-[6px] text-text-4"
              aria-label={t('Suivant', 'Next')}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="mt-3 h-[180px]">
          {isLoading ? (
            <div className="flex h-full items-center justify-center font-mono text-[11px] text-text-4">
              {t('chargement…', 'loading…')}
            </div>
          ) : !hasData ? (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-text-4">
                {t('// aucune donnée', '// no data')}
              </div>
              <p className="text-[11px] text-text-3">
                {tab === 'Runs'
                  ? t('Aucun run sur la période', 'No runs in this period')
                  : t('Pas encore de série quotidienne', 'No daily series yet')}
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 12, right: 4, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id={palette.gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={palette.stroke} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={palette.stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(30 14% 90%)" vertical={false} strokeDasharray="3 4" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'hsl(240 4% 70%)', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <Tooltip
                  formatter={(value: number) =>
                    tab === 'Runs' ? formatDuration(value * 1000) : formatNumber(value)
                  }
                  contentStyle={{
                    background: 'hsl(0 0% 100%)',
                    border: '1px solid hsl(30 14% 90%)',
                    borderRadius: 10,
                    padding: '6px 10px',
                    fontSize: 11,
                    color: 'hsl(240 10% 12%)',
                  }}
                  cursor={{ stroke: palette.stroke, strokeWidth: 1, strokeDasharray: '3 3' }}
                />
                <Area
                  dataKey="value"
                  name={
                    tab === 'Runs'
                      ? t('Durée', 'Duration')
                      : tab === 'Viraux'
                        ? t('Reels viraux', 'Viral reels')
                        : t('Abonnés', 'Followers')
                  }
                  type="monotone"
                  stroke={palette.stroke}
                  strokeWidth={2}
                  fill={`url(#${palette.gradientId})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section className="mt-5 rounded-[16px] border border-line-1 bg-bg-1 p-4 shadow-card">
        <header className="flex items-center justify-between">
          <h3 className="text-[14px] font-semibold tracking-tight text-text-1">
            {t("Journal d'activité", 'Activity log')}
          </h3>
          <button
            type="button"
            aria-label={t('Rechercher', 'Search')}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-line-1 text-text-3 transition-colors hover:text-text-1"
          >
            <Search className="h-3 w-3" />
          </button>
        </header>

        <ul className="mt-3 flex flex-col gap-1">
          {recent.length === 0 ? (
            <li className="py-4 text-center font-mono text-[11px] text-text-3">
              {t('// aucune activité récente', '// no recent activity')}
            </li>
          ) : (
            recent.map((item) => <ActivityRow key={item.id} item={item} />)
          )}
        </ul>
      </section>
    </aside>
  );
}

interface RailActivityItem {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  time: string;
  status: string;
}

function ActivityRow({ item }: { item: RailActivityItem }) {
  useI18n();
  const statusColor =
    item.status === 'OK'
      ? 'text-emerald'
      : item.status === 'RUNNING'
        ? 'text-violet'
        : item.status === 'PARTIAL'
          ? 'text-amber'
          : 'text-rose';
  return (
    <li className="flex items-start gap-3 rounded-[10px] px-2 py-2 transition-colors hover:bg-bg-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-line-1 bg-bg-2 text-[14px]">
        {item.icon}
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className={cn('text-[12px] font-medium', statusColor)}>{item.title}</div>
        <div className="text-[11px] text-text-3">{item.subtitle}</div>
      </div>
      <span className="pt-1 text-[11px] text-text-4">{formatRelative(item.time)}</span>
    </li>
  );
}

function buildRunsChart(runs: RunSummary[]): { label: string; value: number }[] {
  if (runs.length === 0) return [];
  return runs
    .slice()
    .reverse()
    .map((r, i) => ({
      label: `${i + 1}`,
      value: Math.round((r.durationMs ?? 0) / 1000),
    }));
}

function buildDailyChart(
  points: OverviewSeriesPoint[] | undefined,
): { label: string; value: number }[] {
  if (!points || points.length === 0) return [];
  return points.map((p) => {
    // `date` (YYYY-MM-DD) n'est présent qu'en granularity=DAY/WEEK/MONTH — fallback sur le timestamp sinon.
    const label = p.date ? p.date.slice(8, 10) : p.timestamp.slice(8, 10);
    return { label, value: p.value };
  });
}
